import { useBulkTableContext } from '@/components/bulkQueue/BulkTableContext';
import { VisitTableRowFragment } from '@/components/bulkQueue/tables/queries.gql.types';
import { Pagination } from '@/components/bulkQueue/tables/useUnifiedPagination';
import { ServerSideSort } from '@/components/tables/AxleTable';
import { usePaginationByPage } from '@/components/utils/pagination';
import { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import * as Types from 'gql-types';
import { useMemo } from 'react';

/**
 * Shared query result shape for all bulk assignment tables.
 * TVisit may include additional fragment fields (e.g. services table adds
 * appointment/availableServices fields).
 */
type BulkQueryData<TVisit extends VisitTableRowFragment = VisitTableRowFragment> = {
    visits?: { totalCount: number; items: TVisit[] } | null;
};

type UseConnectedBulkTableResult<TVisit extends VisitTableRowFragment> = {
    rows: TVisit[];
    pagination: Pagination;
    serverSideSort: ServerSideSort;
    loading: boolean;
    data: BulkQueryData<TVisit> | undefined;
    showSkeleton: boolean;
};

export type QueryVariables = {
    visitFilters: Types.InputMaybe<Types.VisitFilter>;
    visitPagination: Types.InputMaybe<Types.PaginationInput>;
    visitOrdering: Types.InputMaybe<Types.VisitOrdering>;
};

/**
 * Extracts the common data-fetching logic shared by all bulk assignment tables
 * (clinician, services, tentative date).
 *
 * Each table's Connected* component reduces to:
 *   1. Call this hook with its GQL query
 *   2. Check showSkeleton
 *   3. Wrap rows in its domain-specific context provider
 */
export const useConnectedBulkTable = <
    TData extends BulkQueryData<TVisit>,
    TVisit extends VisitTableRowFragment = VisitTableRowFragment,
>(options: {
    query: TypedDocumentNode<TData, QueryVariables>;
}): UseConnectedBulkTableResult<TVisit> => {
    const { query } = options;

    // 1. Consume filters and sorting from context, set up pagination.
    const { visitFilters, visitOrdering, serverSideSort } = useBulkTableContext();
    const paginationByPage = usePaginationByPage({ defaultPageSize: 25 });

    // 2. Fetch the visits list.
    const { data, previousData, loading } = useQuery<TData, QueryVariables>(query, {
        variables: {
            visitFilters,
            visitPagination: paginationByPage.paginationInput,
            visitOrdering,
        },
        fetchPolicy: 'cache-and-network',
    });
    const currentData = data ?? previousData;

    const rows = currentData?.visits?.items ?? [];

    const pagination: Pagination = useMemo(
        () => ({
            ...paginationByPage.dynamicPaging,
            numRows: currentData?.visits?.totalCount ?? 0,
        }),
        [paginationByPage.dynamicPaging, currentData?.visits?.totalCount],
    );

    return {
        rows,
        pagination,
        serverSideSort,
        loading,
        data: currentData,
        showSkeleton: loading && !currentData,
    };
};
